<soft-ds-progress-steps
  class="w-full mb-4"
  [steps]="steps()"
  [(currentIndex)]="stepIndex"
  [allowJump]="false"
  data-testid="einheiten-umbuchung-steps"
/>

@let artikel = artikelMenge().artikel;
<div class="flex flex-col border rounded w-full p-1.5 bg-surface mb-3">
  <span class="ds-caption color-light">{{ artikel.nummer }}</span>
  <span class="ds-body-1-semibold">{{ artikel.artbez1 }}</span>
</div>

@switch (stepIndex()) {
  @case (0) {
    <soft-resource-container [resource]="stock">
      @if (rows().length === 0) {
        <span class="ds-body-2 color-light" data-testid="einheiten-umbuchung-no-stock">
          {{ '#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.NO_STOCK' | translate }}
        </span>
      } @else {
        <div class="overflow-y-auto max-h-[40vh]">
          <table class="w-full ds-body-2">
            <thead>
              <tr class="border-b">
                <th class="text-left p-2">
                  {{
                    '#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.LAGERPLATZ' | translate
                  }}
                </th>
                <th class="text-left p-2">
                  {{
                    '#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.EINHEIT' | translate
                  }}
                </th>
                <th class="text-right p-2">
                  {{
                    '#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.BESTAND' | translate
                  }}
                </th>
              </tr>
            </thead>
            <tbody>
              @for (row of rows(); track stockRowKey(row)) {
                @let available = row.unit.menge > 0;
                <tr
                  class="border-b"
                  [class]="{
                    'cursor-pointer hover:bg-hover': available,
                    'color-light': !available,
                    '!bg-selected': isSelected(row),
                  }"
                  [attr.tabindex]="available ? 0 : null"
                  [attr.aria-disabled]="!available"
                  [attr.data-testid]="'einheiten-umbuchung-row-' + stockRowKey(row)"
                  (click)="selectSource(row)"
                  (keydown.enter)="selectSource(row)"
                >
                  <td class="p-2">
                    <div class="flex flex-col">
                      <span>{{ row.lagerplatz.lagerplatz }}</span>
                      <soft-batch-info [charge]="row.charge" [mhd]="row.mhd" />
                    </div>
                  </td>
                  <td class="p-2">
                    <div class="flex flex-col">
                      <span>{{ row.unit.einheit.arteh }}</span>
                      @if (formatDimension(row.unit.dimension); as dimension) {
                        <span class="ds-caption color-light">{{ dimension }}</span>
                      }
                    </div>
                  </td>
                  <td class="p-2 text-right">{{ row.unit.menge }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </soft-resource-container>
  }

  @case (1) {
    @if (source(); as source) {
      <div class="flex flex-col gap-2">
        <span class="ds-body-2 color-light">
          {{ source.lagerplatz.lagerplatz }} · {{ source.unit.einheit.arteh }}
          @if (formatDimension(source.unit.dimension); as dimension) {
            · {{ dimension }}
          }
        </span>
        <soft-batch-info [charge]="source.charge" [mhd]="source.mhd" />
        <span class="soft-overline">
          {{ '#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.QUELLMENGE' | translate }}
        </span>
        <soft-ds-quantity-input
          [value]="quantity()"
          [min]="0"
          [unit]="source.unit.einheit.arteh"
          [helperText]="stockHint()"
          (valueChange)="quantity.set($event ?? 0)"
          data-testid="einheiten-umbuchung-quantity"
        />
      </div>
    }
  }

  @case (2) {
    <div class="flex flex-col gap-3">
      <span class="soft-overline">
        {{ '#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.ZIEL_EINHEIT' | translate }}
      </span>

      @for (row of targetRows(); track row.unit.einheit.id) {
        <div
          class="flex flex-col gap-2 border rounded p-2"
          [attr.data-testid]="'einheiten-umbuchung-target-' + row.unit.einheit.id"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="flex flex-col">
              <span class="ds-body-1-semibold">{{ row.unit.einheit.arteh }}</span>
              @if (formatDimension(row.unit.dimension); as dimension) {
                <span class="ds-caption color-light">{{ dimension }}</span>
              }
            </div>
            @if (targetRows().length > 1) {
              <soft-ds-button
                variant="ghost"
                size="sm"
                [attr.aria-label]="
                  '#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.REMOVE_UNIT' | translate
                "
                (click)="removeTargetRow(row)"
                [attr.data-testid]="'einheiten-umbuchung-remove-' + row.unit.einheit.id"
              >
                <ng-template softSlot="leading">
                  <soft-ds-icon name="fa-regular fa-xmark" />
                </ng-template>
              </soft-ds-button>
            }
          </div>

          @if (row.proposal?.hinweis; as hinweis) {
            <span class="ds-body-2 color-light" data-testid="einheiten-umbuchung-hint">
              {{ hinweis }}
            </span>
          }
          <span class="soft-overline">
            {{ '#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.ZIELMENGE' | translate }}
          </span>
          <soft-ds-quantity-input
            [value]="row.quantity"
            [min]="0"
            [max]="proposedQuantity(row)"
            [unit]="row.unit.einheit.arteh"
            [disabled]="row.loading || !row.proposal"
            (valueChange)="setTargetQuantity(row, $event ?? 0)"
            [attr.data-testid]="'einheiten-umbuchung-target-quantity-' + row.unit.einheit.id"
          />
          @if (row.proposal?.restMenge) {
            <span class="ds-caption color-light">
              {{ '#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.REST' | translate }}:
              {{ row.proposal?.restMenge }} {{ row.proposal?.restEinheit?.arteh }}
            </span>
          }
        </div>
      }

      @if (remainingTargetUnits().length > 0) {
        <soft-ds-button
          variant="outline"
          size="sm"
          class="self-start"
          (click)="addTargetUnit()"
          data-testid="einheiten-umbuchung-add-unit"
        >
          <ng-template softSlot="leading">
            <soft-ds-icon name="fa-regular fa-plus" />
          </ng-template>
          {{ '#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.ADD_UNIT' | translate }}
        </soft-ds-button>
      }
    </div>
  }

  @case (3) {
    <div class="flex flex-col gap-2">
      <span class="soft-overline">
        {{ '#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.ZIEL_LAGERPLATZ' | translate }}
      </span>
      <soft-lagerplatz-suche
        [lagerplatz]="targetLocation()"
        [storageId]="idlagerstamm()"
        (lagerplatzChange)="targetLocation.set($event)"
      />

      @if (source(); as source) {
        <div
          class="flex flex-col border rounded p-2 mt-2"
          data-testid="einheiten-umbuchung-summary"
        >
          <span class="ds-body-2">
            {{ quantity() }} {{ source.unit.einheit.arteh }} ({{
              source.lagerplatz.lagerplatz
            }})
          </span>
          @for (row of targetRows(); track row.unit.einheit.id) {
            @if (row.quantity > 0) {
              <span class="ds-body-2">
                → {{ row.quantity }} {{ row.unit.einheit.arteh }} ({{
                  targetLocation()?.lagerplatz
                }})
              </span>
            }
          }
        </div>
      }
    </div>
  }
}

<ng-template #bottomTemplate>
  <div class="flex w-full gap-2 mt-3 mb-1">
    @if (stepIndex() > 0) {
      <soft-ds-button
        class="flex-1"
        variant="outline"
        (click)="back()"
        data-testid="einheiten-umbuchung-back"
      >
        {{ '#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.BACK' | translate }}
      </soft-ds-button>
    }
    @if (isLastStep()) {
      <soft-ds-button
        class="flex-1"
        [disabled]="!canContinue()"
        (click)="book()"
        data-testid="einheiten-umbuchung-book"
      >
        {{ '#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.BOOK' | translate }}
      </soft-ds-button>
    } @else {
      <soft-ds-button
        class="flex-1"
        [disabled]="!canContinue()"
        (click)="next()"
        data-testid="einheiten-umbuchung-next"
      >
        {{ '#KOMMISSIONIERUNG.DIALOGS.EINHEITEN_UMBUCHUNG.NEXT' | translate }}
      </soft-ds-button>
    }
  </div>
</ng-template>
